import { useEffect, useMemo, useState } from "react";
import { PageHeader, SectionDivider } from "@/components/primitives";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import BloomUploadDialog from "@/components/BloomUploadDialog";
import WavyFrame from "@/components/WavyFrame";
import { Plus, Trash2, Users, Clock } from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const MAX_PX = 88;   // ~half of previous, fits ~200 blooms easily
const MIN_PX = 22;

export default function Community() {
  const { user } = useAuth();
  const [blooms, setBlooms] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/community/blooms");
      setBlooms(data);
    } catch {
      toast.error("Could not load the flat lay");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const sized = useMemo(() => {
    if (!blooms.length) return [];
    const released = blooms.filter((b) => b.released || true); // owner sees own pending
    const maxCm = released.reduce((m, b) => Math.max(m, b.bloom_diameter_cm), 0) || 1;
    return released
      .slice()
      .sort((a, b) => b.bloom_diameter_cm - a.bloom_diameter_cm)
      .map((b) => {
        const px = Math.max(MIN_PX, (b.bloom_diameter_cm / maxCm) * MAX_PX);
        return { ...b, px };
      });
  }, [blooms]);

  const openQuestions = useMemo(
    () => blooms.filter((b) => b.kind === "question" && b.released),
    [blooms]
  );

  const onRemove = async (id) => {
    try {
      await api.delete(`/community/blooms/${id}`);
      setBlooms((prev) => prev.filter((b) => b.id !== id));
      toast.success("Bloom lifted from the flat lay");
    } catch {
      toast.error("Could not remove");
    }
  };

  return (
    <div data-testid="community-page">
      <PageHeader
        kicker="The Community"
        title="The Flat Lay"
        subtitle="Every keeper's blooms, scaled to true relative size"
      />

      {openQuestions.length > 0 && (
        <div className="h-card p-4 mb-5 border-l-4 border-terracotta" data-testid="community-questions-banner">
          <div className="kicker text-terracotta-dark text-[11px] mb-1">?  Help identify</div>
          <div className="font-display text-lg text-ink">
            {openQuestions.length} bloom{openQuestions.length === 1 ? "" : "s"} waiting to be identified — scroll down for questions, or write to the keeper if you recognise one.
          </div>
        </div>
      )}

      <div className="flex items-center justify-between gap-3 -mt-2 mb-6">
        <div className="text-ink-soft text-base flex items-center gap-2">
          <Users size={14} className="text-sage-dark" />
          <span data-testid="community-count">
            {blooms.filter((b) => b.released).length} {blooms.filter((b) => b.released).length === 1 ? "bloom" : "blooms"} on the cloth
          </span>
        </div>
        <BloomUploadDialog onAdded={(b) => setBlooms((prev) => [b, ...prev])}>
          <button className="btn-heritage text-sm py-2 px-4" data-testid="community-add-btn">
            <Plus size={14} /> Add your bloom
          </button>
        </BloomUploadDialog>
      </div>

      {/* The cloth — wrapped in a wavy green frame */}
      <div className="relative" data-testid="community-frame-wrap">
        {/* Frame sits behind everything */}
        <div className="absolute -inset-3 sm:-inset-5 pointer-events-none">
          <WavyFrame color="#5F6B43" strokeWidth={0.55} waves={8} amp={1.6} gap={1.4} inset={2.2} />
        </div>

        <div
          className="relative h-card p-5 sm:p-8 overflow-hidden"
          style={{
            background:
              "radial-gradient(circle at 30% 20%, rgba(126,140,91,0.08), transparent 60%)," +
              "radial-gradient(circle at 80% 80%, rgba(194,107,74,0.07), transparent 55%)," +
              "#FBF7EC",
            minHeight: "60vh",
          }}
          data-testid="community-canvas"
        >
        {loading ? (
          <div className="text-center font-display italic text-ink-soft py-20">
            Unfolding the cloth...
          </div>
        ) : sized.length === 0 ? (
          <EmptyCloth />
        ) : (
          <div className="flex flex-wrap gap-6 sm:gap-8 items-center justify-center py-6">
            {sized.map((b) => (
              <BloomBadge key={b.id} bloom={b} isOwner={b.user_id === user?.id} onRemove={() => onRemove(b.id)} />
            ))}
          </div>
        )}
        </div>
      </div>

      <div className="mt-8">
        <SectionDivider label="A note from the keeper" />
      </div>
      <p className="text-center text-ink-soft font-display italic text-lg mt-3">
        Sizes are true to life. A Café au Lait at 22cm sits next to a 5cm pompon, the way they would in your hand.
      </p>
    </div>
  );
}

function BloomBadge({ bloom, isOwner, onRemove }) {
  const pending = isOwner && !bloom.released;
  return (
    <figure className="flex flex-col items-center text-center gap-2" data-testid={`bloom-${bloom.id}`}>
      <div className="relative" style={{ width: bloom.px, height: bloom.px }}>
        <img
          src={bloom.image_data_url}
          alt={bloom.variety_name}
          className="w-full h-full object-cover"
          style={{
            filter: "drop-shadow(0 8px 16px rgba(95,107,67,0.18))",
            opacity: pending ? 0.55 : 1,
          }}
          draggable={false}
        />
        {pending && (
          <span className="absolute inset-x-0 -bottom-1 mx-auto w-fit px-2 py-0.5 text-[10px] rounded-full bg-sage-dark text-cream-soft flex items-center gap-1 tracking-wider uppercase" data-testid={`bloom-pending-${bloom.id}`}>
            <Clock size={10} /> Awaiting release
          </span>
        )}
        {isOwner && (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <button
                className="absolute -top-1 -right-1 w-7 h-7 rounded-full bg-cream-soft border border-heritage-border flex items-center justify-center hover:bg-cream-deep text-terracotta"
                aria-label="Remove your bloom"
                data-testid={`bloom-remove-${bloom.id}`}
              >
                <Trash2 size={13} />
              </button>
            </AlertDialogTrigger>
            <AlertDialogContent className="bg-cream-soft border-heritage-border">
              <AlertDialogHeader>
                <AlertDialogTitle className="font-display text-2xl text-sage-dark">Lift this bloom?</AlertDialogTitle>
                <AlertDialogDescription className="text-ink-soft">
                  It will be removed from the community flat lay.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel className="btn-ghost">Keep it</AlertDialogCancel>
                <AlertDialogAction onClick={onRemove} className="btn-heritage" data-testid={`bloom-remove-confirm-${bloom.id}`}>
                  Lift
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </div>
      <figcaption className="leading-tight">
        <div className="font-display text-base sm:text-lg text-sage-dark">{bloom.variety_name}</div>
        <div className="kicker text-[10px]">{bloom.bloom_diameter_cm}cm</div>
        <div className="font-display italic text-xs text-ink-soft mt-0.5">
          by {bloom.user_name}{bloom.user_garden ? `, ${bloom.user_garden}` : ""}
        </div>
      </figcaption>
    </figure>
  );
}

function EmptyCloth() {
  return (
    <div className="text-center py-12" data-testid="community-empty">
      <div className="kicker mb-2">The cloth is laid</div>
      <p className="font-display italic text-ink-soft text-xl max-w-md mx-auto">
        Be the first to pin a bloom. Pop in a photograph, the variety name, and the real diameter in centimetres.
      </p>
    </div>
  );
}
